<template>
  <div class="fish-hud" :class="{ 'fish-hud--compacto': compacto }">
    <!--
      1. ENERGIA — o que limita a próxima ação, então vem primeiro.

      O número fica **ao lado** do medidor, nunca dentro dele. A primeira versão passava
      `mostrar-numero` a um medidor `fino` (16px de altura, com 6px de moldura de cada lado): o
      texto era desenhado em quatro pixels de espaço útil e saía ilegível. Um medidor de HUD é
      estreito por natureza — o número precisa de caixa própria.
    -->
    <div class="fish-hud__peca fish-hud__energia">
      <FishIcon nome="atributo-energia" :tamanho="tamanhoDoIcone" />
      <span class="t-numero fish-hud__valor fish-hud__valor--energia">
        {{ energia }}<small>/{{ energiaMaxima }}</small>
      </span>
      <FishMeter
        v-if="!compacto"
        :valor="energia"
        :maximo="energiaMaxima"
        cor="agua"
        tamanho="fino"
        rotulo-acessivel="Energia"
        class="fish-hud__medidor"
      />
      <!--
        O RELÓGIO da energia: quanto falta para o próximo ponto cair.

        A regeneração é preguiçosa no servidor — nada empurra o número novo — e o `StaminaBar`
        antigo mostrava esta contagem. Ela sumiu com ele (04/09) e voltou a pedido (07/09/2026):
        sem o relógio a barra parece travada, mesmo quando está subindo de cinco em cinco minutos.
        Some quando a barra está cheia, porque aí não há o que esperar.
      -->
      <span
        v-if="proximaRecargaEm && energia < energiaMaxima"
        class="t-legenda fish-hud__relogio"
        :title="$t('header.energy.clock', { n: recargaPorTick, tempo: contagem })"
      >
        <FishIcon nome="clock" tamanho="miudo" />
        <span class="t-numero">{{ contagem }}</span>
        <small class="fish-hud__relogio-ganho">+{{ recargaPorTick }}</small>
      </span>
    </div>

    <!-- 2. OURO — a restrição de tudo o que se compra. -->
    <button class="fish-hud__peca fish-hud__ouro" type="button" v-bind="rotuloExato('Ouro', ouro)" @click="$emit('ouro')">
      <span class="fish-hud__bolsa repouso-respira">
        <FishIcon nome="coins" :tamanho="tamanhoDoIcone" />
      </span>
      <span class="t-numero fish-hud__valor">{{ formatar(ouro) }}</span>
    </button>

    <!--
      O PARALLELIUM aparece SÓ ONDE SE GASTA (D6).

      "Vira três: energia, ouro, perfil. Nível e XP moram no perfil; parallelium aparece onde se
      gasta." Ele não some do jogo — some do HUD permanente, onde competia por atenção sem que o
      jogador pudesse fazer nada a respeito na maioria das telas.
    -->
    <button
      v-if="mostrarParallelium"
      class="fish-hud__peca fish-hud__parallelium"
      type="button"
      v-bind="rotuloExato('Parallelium', parallelium)"
      @click="$emit('parallelium')"
    >
      <FishIcon nome="gem" :tamanho="tamanhoDoIcone" />
      <span class="t-numero fish-hud__valor">{{ formatar(parallelium) }}</span>
    </button>

    <!--
      3. PERFIL — e é aqui que o nível mora agora (D6).

      O nível fica **ao lado** do retrato, não sobreposto a ele: a primeira versão punha a caixinha
      do nível por cima do canto do avatar e o resultado lia como dois elementos quebrados
      encostados, não como um badge.
    -->
    <button
      class="fish-hud__peca fish-hud__perfil"
      type="button"
      :aria-label="`Perfil de ${nome}, nível ${nivel}, ${experiencia} de ${experienciaMaxima} de experiência`"
      :title="experienciaMaxima ? `XP: ${formatar(experiencia)} / ${formatar(experienciaMaxima)}` : null"
      @click="$emit('perfil')"
    >
      <img v-if="retrato" class="fish-hud__retrato suavizada" :src="retrato" alt="" />
      <FishIcon v-else nome="user" :tamanho="tamanhoDoIcone" />
      <span class="fish-hud__nivel t-numero">
        <small>Nv</small>{{ nivel }}
      </span>
      <!-- O XP no próprio chip (pedido do autor, 03/09: "legal saber o nível, mas quanto de exp?"):
           um fio dourado enchendo rumo ao próximo nível, com o número exato no title/aria. -->
      <span
        v-if="experienciaMaxima > 0"
        class="fish-hud__xp"
        role="progressbar"
        :aria-valuenow="experiencia"
        aria-valuemin="0"
        :aria-valuemax="experienciaMaxima"
      >
        <span class="fish-hud__xp-fio" :style="{ width: `${Math.min(100, (experiencia / experienciaMaxima) * 100)}%` }" />
      </span>
    </button>
  </div>
</template>

<script>
import FishIcon from './FishIcon.vue';
import FishMeter from './FishMeter.vue';

/**
 * 3.7 — `FishHud`: **um só**, extraído do Header (UI-16, D6).
 *
 * ## O defeito
 *
 * O `Header.vue` já tinha um HUD bom e completo — `level-badge`, `exp-bar`, `gold-pouch`,
 * `paralleliums-pouch`, com brilho e partícula. Mas **a Loja, Melhorias e o Inventário desenhavam
 * cada um a sua própria pílula de ouro**, em posição e estilo diferentes, em vez de reutilizar o do
 * header. Quatro implementações da mesma informação, que é como uma UI passa a parecer quatro UIs.
 *
 * ## O corte do D6
 *
 * O header de hoje tem **seis elementos competindo**: nível, XP, energia, ouro, parallelium e
 * engrenagem. Vira **três**:
 *
 * | Fica | Por quê |
 * |---|---|
 * | **energia** | é o que limita a próxima ação |
 * | **ouro** | é a restrição de tudo o que se compra |
 * | **perfil** | é a porta para o resto, e o nível mora dentro dele |
 *
 * **Nível e XP vão para o perfil.** Eles são orgulho, não decisão: ninguém escolhe o que fazer
 * olhando a barra de XP. O nível continua visível como o número no retrato, que é o suficiente.
 *
 * **O parallelium aparece onde se gasta** (`mostrarParallelium`), não sempre.
 *
 * **A engrenagem sai do HUD** e vai para o "Mais" (D12), junto de Configurações.
 *
 * ## Um primitivo, então sem store (D4)
 *
 * Ele recebe props. Quem tem o `mapGetters` é o Header — e é o que permite ao Inventário e à Loja
 * usarem o mesmo componente sem herdar a dependência de Vuex do cabeçalho.
 */
export default {
  name: 'FishHud',
  components: { FishIcon, FishMeter },
  props: {
    energia: { type: Number, default: 0 },
    energiaMaxima: { type: Number, default: 100 },
    ouro: { type: Number, default: 0 },
    parallelium: { type: Number, default: 0 },
    nivel: { type: Number, default: 1 },
    experiencia: { type: Number, default: 0 },
    experienciaMaxima: { type: Number, default: 0 },
    nome: { type: String, default: 'Pescador' },
    retrato: { type: String, default: '' },
    /** Só nas telas onde ele se gasta: Loja, Moedas, Roleta, itens premium (D6). */
    mostrarParallelium: { type: Boolean, default: false },
    /** Versão estreita, para dentro de outra tela em vez do topo. */
    compacto: { type: Boolean, default: false },
    /** Instante (ms, `Date.now()`) em que o próximo ponto de energia cai. Zero = não sei. */
    proximaRecargaEm: { type: Number, default: 0 },
    /** Quantos pontos caem por tique. */
    recargaPorTick: { type: Number, default: 1 },
  },
  emits: ['ouro', 'parallelium', 'perfil'],
  data() {
    return { agora: Date.now(), relogio: null };
  },
  computed: {
    /** `mm:ss` até o próximo ponto. Nunca negativo: passou da hora, mostra 00:00 até a releitura. */
    contagem() {
      const faltam = Math.max(0, Math.ceil((this.proximaRecargaEm - this.agora) / 1000));
      const m = Math.floor(faltam / 60);
      const s = faltam % 60;
      return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    },
    /** 16px no telefone: cada peça devolve 8px ao número, e são quatro peças numa viga de 360. */
    tamanhoDoIcone() {
      return this.compacto ? 'miudo' : 'pequeno';
    },
  },
  mounted() {
    // Um segundo, porque é um relógio. O `setInterval` vive aqui e não na store: é desenho, não
    // dado — a store tem o SEU relógio, que relê o perfil na hora do tique.
    this.relogio = setInterval(() => {
      this.agora = Date.now();
    }, 1000);
  },
  beforeUnmount() {
    clearInterval(this.relogio);
  },
  methods: {
    /**
     * O número do mostrador, abreviado para caber.
     *
     * Acima de 100 mil ele vira "128k": o HUD tem largura fixa e um valor de sete dígitos a
     * estoura. Abaixo disso o valor exato importa — é com ele que se decide uma compra.
     *
     * **O milhão não tinha degrau** (28/09/2026): 9,6 milhões saíam "9601k", cinco dígitos e uma
     * letra, e no telefone o mostrador cortava no meio — "o gold eu não consigo ver quanto tenho".
     * Agora é "9,6M". No `compacto` (telefone) o corte desce para 10 mil, porque a viga de 360px
     * dá ~40px de número a cada peça: "12k" cabe, "12.345" não.
     *
     * Sempre para BAIXO (`floor`): arredondar 9.960.000 para "10M" prometeria um ouro que o
     * jogador não tem, e ele descobriria na hora de comprar. O valor exato fica no `title`.
     */
    formatar(n) {
      const v = Math.floor(Number(n) || 0);
      const corte = this.compacto ? 10000 : 100000;
      if (v < corte) return v.toLocaleString('pt-BR');
      if (v < 1e6) return `${Math.floor(v / 1000)}k`;
      const [base, letra] = v < 1e9 ? [1e6, 'M'] : [1e9, 'B'];
      const x = v / base;
      // Quatro caracteres no compacto ("9,6M", "96M"); até cinco na viga larga ("9,61M").
      const casas = this.compacto ? (x < 10 ? 1 : 0) : x < 10 ? 2 : x < 100 ? 1 : 0;
      const fator = 10 ** casas;
      const truncado = Math.floor(x * fator) / fator;
      return `${truncado.toLocaleString('pt-BR', { maximumFractionDigits: casas })}${letra}`;
    },
    /** O valor exato para leitor de tela e para o dedo que segura a peça: o mostrador abrevia. */
    rotuloExato(nome, n) {
      const exato = `${nome}: ${(Math.floor(Number(n) || 0)).toLocaleString('pt-BR')}`;
      return { 'aria-label': exato, title: exato };
    },
  },
};
</script>

<style scoped>
.fish-hud {
  display: flex;
  align-items: center;
  gap: var(--e-2);
  min-width: 0;
}

/**
 * A peça do HUD é um **mostrador embutido na viga**, e usa a arte do pack para isso.
 *
 * É a mesma placa do `FishSlot` (`encaixe.png`): a arte do Book UI System com a luz e a sombra
 * invertidas, ou seja, uma peça que **afunda**. É o que um mostrador é — um recorte na madeira com
 * um vidro de água atrás —, e é por isso que ele usa a mesma arte do encaixe em vez de a do botão:
 * o botão sobe porque se aperta; o mostrador afunda porque só se lê.
 *
 * A primeira versão eram três `div`s de borda 2px. Elas até tinham bisel, mas bisel de CSS numa
 * caixa retangular é bisel de CSS numa caixa retangular — não tem colchete de canto nem espessura,
 * que é o que faz o pack parecer feito à mão.
 */
.fish-hud__peca {
  display: flex;
  align-items: center;
  gap: var(--e-2);
  min-height: 40px;
  min-width: 0;
  padding: 0 var(--e-2);

  border-style: solid;
  border-color: transparent;
  border-width: var(--placa-borda);
  border-image-source: url('/ui/encaixe.png');
  border-image-slice: var(--placa-fatia);
  border-image-width: var(--placa-borda);
  border-image-repeat: repeat;
  background: var(--fundo-encaixe);
  background-clip: padding-box;
  image-rendering: pixelated;
  color: var(--texto);
}

button.fish-hud__peca {
  cursor: pointer;
  transition: filter var(--t-rapido) var(--curva-suave), transform var(--t-rapido) var(--curva-quique);
}

@media (hover: hover) {
  button.fish-hud__peca:hover {
    filter: brightness(1.25);
  }
}

button.fish-hud__peca:active {
  transform: translateY(2px);
}

.fish-hud__energia {
  max-width: 220px;
}

.fish-hud__medidor {
  min-width: 56px;
}

/**
 * O valor.
 *
 * `--f-3` (16px) e não `--f-2`: a DotGothic16 é uma fonte de grade de 16px, e **abaixo de 16px ela
 * perde traço** — os dígitos ficam com um pixel de espessura e a leitura de relance some. O HUD é
 * o texto mais consultado do jogo; ele é o último lugar onde economizar altura.
 *
 * `tabular-nums` vem da classe `t-numero`: sem ela o valor do ouro PULA de posição a cada moeda
 * ganha, porque "1" é mais estreito que "8" — e movimento involuntário é o que o D0 não quer.
 */
.fish-hud__valor {
  font-size: var(--f-3);
  white-space: nowrap;
}

/* O denominador ("/30") é apoio, não valor: ele encolhe e apaga, senão compete com o número que
 * importa. */
.fish-hud__valor small {
  font-size: var(--f-1);
  color: var(--texto-fraco);
}

.fish-hud__valor--energia {
  color: var(--agua-espuma);
}

/* O relógio: apoio, ao lado do medidor. Tabular para o segundo não pular de lugar. */
.fish-hud__relogio {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  white-space: nowrap;
  color: var(--texto-fraco);
}

.fish-hud__relogio-ganho {
  font-size: var(--f-1);
  color: var(--agua-espuma);
}

.fish-hud__ouro .fish-hud__valor {
  color: var(--ouro-claro);
}

.fish-hud__parallelium .fish-hud__valor {
  color: var(--parallelium-claro);
}

/* A bolsa respira (D10-3). É o objeto que mais recebe atenção do jogador, e um objeto inerte até
 * ser clicado é a assinatura de aplicativo. */
.fish-hud__bolsa {
  line-height: 0;
}

.fish-hud__perfil {
  gap: var(--e-2);
}

.fish-hud__retrato {
  width: 28px;
  height: 28px;
  object-fit: cover;
  border: 2px solid var(--latao);
}

/* O nível: o que sobra do `level-badge` depois do corte do D6. Ao LADO do retrato — sobreposto ao
 * canto ele lia como dois elementos quebrados encostados, não como um badge. */
.fish-hud__nivel {
  font-size: var(--f-3);
  color: var(--ouro-claro);
  line-height: 1;
}

/* O fio de XP: um trilho fino colado na base do chip, enchendo a ouro rumo ao próximo nível. */
.fish-hud__perfil {
  position: relative;
}

.fish-hud__xp {
  position: absolute;
  left: 4px;
  right: 4px;
  bottom: 2px;
  height: 3px;
  background: rgba(0, 0, 0, 0.45);
  overflow: hidden;
}

.fish-hud__xp-fio {
  display: block;
  height: 100%;
  background: var(--ouro);
  transition: width var(--t-lento) var(--curva-suave);
}

.fish-hud__nivel small {
  font-size: var(--f-1);
  color: var(--texto-fraco);
  margin-right: 2px;
}

/* No telefone o medidor some e sobra o número: é o corte certo, porque o número responde "posso
 * pescar?" e a barra só responde "quão cheio". */
/*
 * A VIGA DE 360px (28/09/2026).
 *
 * Emblema e quatro peças numa tela de 360: sobravam ~15px de número por peça, e o valor era
 * cortado pela borda da peça ("1185|", "4.31"). O corte agora é distribuído: ícone de 16px, respiro
 * lateral de 4px, 4px entre peças, o ouro abreviado mais cedo (`formatar`), e o relógio da energia
 * DEBAIXO do número em vez de ao lado — "2 ⏱00:28 +1" numa linha só era a peça mais larga da viga.
 *
 * O ouro e o parallelium não encolhem: são os números que decidem compra. Se ainda faltar largura
 * (320px), quem cede é a energia e o perfil, que têm número curto.
 */
.fish-hud--compacto {
  gap: var(--e-1);
}

.fish-hud--compacto .fish-hud__peca {
  padding: 0 var(--e-1);
  gap: var(--e-1);
}

.fish-hud--compacto .fish-hud__ouro,
.fish-hud--compacto .fish-hud__parallelium {
  flex-shrink: 0;
}

.fish-hud--compacto .fish-hud__energia {
  display: grid;
  grid-template-columns: auto auto;
  align-items: center;
  column-gap: var(--e-1);
  row-gap: 0;
}

.fish-hud--compacto .fish-hud__energia > .fish-icon {
  grid-row: 1 / span 2;
}

.fish-hud--compacto .fish-hud__valor--energia,
.fish-hud--compacto .fish-hud__relogio {
  line-height: 1.05;
}

.fish-hud--compacto .fish-hud__relogio {
  font-size: var(--f-1);
}

/* No compacto o relógio é só o mm:ss: o ícone e o "+1" moram no `title`. */
.fish-hud--compacto .fish-hud__relogio > .fish-icon,
.fish-hud--compacto .fish-hud__relogio-ganho {
  display: none;
}

.fish-hud--compacto .fish-hud__retrato {
  width: 20px;
  height: 20px;
}

.fish-hud--compacto .fish-hud__nivel small,
.fish-hud--compacto .fish-hud__valor small {
  display: none;
}
</style>
